import React, { Fragment, useContext, useId } from 'react';
import { matchPath, NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { VerticalNav } from '@bynder/design-system';
import {
    IconCollection,
    IconColorStyle,
    IconFolder,
    IconHelpOutline,
    IconMasonry,
    IconNotifications,
    IconUpload,
    IconUserGroup,
    IconVideoTemplate,
} from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import { getCreatives } from 'packages/store/creatives/creatives.selectors';
import AuthorizationHelper from '~/helpers/AuthorizationHelper';
import {
    getActivityFeedDefaultLink,
    getBrandColorsLink,
    getCategoriesLink,
    getDesignsDefaultLink,
    getProjectsDefaultLink,
    getSharedWithMeDefaultLink,
    getUploadsDefaultLink,
} from '~/helpers/defaultLinks';
import features from '~/configs/features';
import permissions from '~/configs/permissions';
import useAccessRights from 'packages/hooks/useAccessRights';
import roles from '~/configs/roles';
import { SkeletonContext } from 'packages/skeleton/SkeletonContext';
import { GetDefaultSidebarItems, SidebarGroupItemType, SidebarItemType, SidebarProps } from './types';
import { StyledSectionDivider, StyledSectionTitle, StyledSidebar } from './Sidebar.styled';
import SidebarSkeleton from './SidebarSkeleton';

const filterHidden = <T extends { hide?: boolean }>(item: T) => !item.hide;
const getDefaultSidebarItems: GetDefaultSidebarItems = (isPlatformAllowed, isAccountRoleAssigned) => ({
    top: [
        {
            title: 'pages.sidebar.designs',
            to: getDesignsDefaultLink(),
            icon: <IconMasonry size="24" />,
            hide: !AuthorizationHelper.hasPermission(permissions.CREATIVES.READ),
        },
        {
            title: 'pages.sidebar.sharedWithMe',
            to: getSharedWithMeDefaultLink(),
            icon: <IconUserGroup />,
            hide: !AuthorizationHelper.hasPermission(permissions.CREATIVES.READ),
        },
        {
            title: 'pages.sidebar.projects',
            to: getProjectsDefaultLink(),
            icon: <IconFolder />,
            hide: !AuthorizationHelper.hasPermission(permissions.CREATIVES.READ),
        },
        {
            title: 'pages.sidebar.assets',
            to: getUploadsDefaultLink(),
            icon: <IconCollection />,
            hide:
                !AuthorizationHelper.hasPermission(permissions.ASSETS.READ) ||
                !AuthorizationHelper.isFeatureAvailable(features.INTERNAL_ASSET),
            matchPath: ['/assets', '/uploads', '/collections'],
        },
        {
            title: 'pages.sidebar.exports',
            to: '',
            icon: <IconUpload />,
            hide: true,
        },
        {
            title: 'pages.sidebar.activity',
            to: getActivityFeedDefaultLink(),
            icon: <IconNotifications />,
            hide: false,
        },
        {
            isGroup: true,
            title: 'pages.sidebar.brand.group.title',
            items: [
                {
                    title: 'pages.sidebar.categories',
                    to: getCategoriesLink(),
                    hide: isAccountRoleAssigned(roles.video_brand_studio.video.manage),
                    icon: <IconVideoTemplate />,
                },
                {
                    title: 'pages.sidebar.brand.colors',
                    to: getBrandColorsLink(),
                    hide: isAccountRoleAssigned(roles.video_brand_studio.video.manage),
                    icon: <IconColorStyle />,
                },
            ].filter(filterHidden),
        },
        {
            isGroup: true,
            title: 'pages.sidebar.help.group.title',
            items: [
                {
                    title: 'pages.sidebar.help.center',
                    to: '',
                    href: 'https://academy.bynder.com/guidelines/guide/05eacb94-3175-4f7d-b785-08d16735fe05/page/14a581bc-d2b6-4ffe-9385-da400652acda',
                    target: '_blank',
                    hide: false,
                    icon: <IconHelpOutline />,
                },
            ],
        },
    ].filter(filterHidden),
    bottom: [].filter(filterHidden),
});

const pathToRegExp = (path: string) => {
    const pattern = path.replace(/:[^:\/]*/g, '[^/]*').replace(/\//g, '\\/');

    return new RegExp(`\^${pattern}\/?$`, 'i');
};

const hidePaths: RegExp[] = [
    '/designs/:creativeId',
    '/designs/:creativeId/edit',
    '/templates/:creativeId/edit',
    // ...
].map(pathToRegExp);

const hasSidebar = (path: string) => !hidePaths.some((pattern) => pattern.test(path));

const sanitizeUrl = (url?: string) => {
    try {
        if (!url) {
            return null;
        }

        const parsedUrl = new URL(url);

        if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
            return null;
        }

        return parsedUrl.toString();
    } catch {
        return null;
    }
};

const Sidebar = ({ pathname, getSidebarItems = getDefaultSidebarItems }: SidebarProps) => {
    const { isPlatformAllowed, isAccountRoleAssigned } = useAccessRights();
    const { translate } = useTranslate();
    const { skeletonShow } = useContext(SkeletonContext);
    const { loadingData } = useSelector(getCreatives);
    const sanitizedPath = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
    const id = useId();

    if (!hasSidebar(sanitizedPath)) {
        return null;
    }

    const isActive = (item: SidebarItemType) => {
        if (item.matchPath) {
            return item.matchPath.some(
                (path) =>
                    !!matchPath(
                        {
                            path: path.split('?')[0],
                            end: true,
                            caseSensitive: false,
                        },
                        sanitizedPath,
                    ),
            );
        }

        return !!matchPath(
            {
                path: item.to.split('?')[0],
                end: true,
                caseSensitive: false,
            },
            sanitizedPath,
        );
    };

    const preventRaceConditionOnClick = (item: SidebarItemType) => (e?: Event) => {
        if (isActive(item)) {
            e?.preventDefault();
        }

        const isDesign = matchPath(
            {
                path: getDesignsDefaultLink(),
                strict: false,
            },
            sanitizedPath,
        );
        const isShared = matchPath(
            {
                path: getSharedWithMeDefaultLink(),
                strict: false,
            },
            sanitizedPath,
        );

        if (loadingData && (isDesign || isShared)) {
            e?.preventDefault();
        }
    };

    const renderSidebarItem = (item: SidebarItemType, idx: number) => {
        const sanitizedUrl = sanitizeUrl(item.href);

        if (sanitizedUrl) {
            return (
                <VerticalNav.Item
                    key={item.href || idx}
                    as="a"
                    href={sanitizedUrl}
                    target={item.target}
                    icon={item.icon}
                    data-testid="page sidebar item"
                >
                    {typeof item.title === 'string' ? translate(item.title) : item.title}
                </VerticalNav.Item>
            );
        }

        return (
            <VerticalNav.Item
                key={item.to || idx}
                as={NavLink}
                to={item.to}
                icon={item.icon}
                onClick={preventRaceConditionOnClick(item)}
                isActive={isActive(item)}
                data-testid="page sidebar item"
            >
                {typeof item.title === 'string' ? translate(item.title) : item.title}
            </VerticalNav.Item>
        );
    };

    const renderGroup = (item: SidebarGroupItemType, idx: number) =>
        !item.hide &&
        item.items.some((sidebarItem) => !sidebarItem.hide) && (
            <Fragment key={idx}>
                <StyledSectionDivider />
                <VerticalNav.Title>
                    <StyledSectionTitle>
                        {typeof item.title === 'string' ? translate(item.title) : item.title}
                    </StyledSectionTitle>
                </VerticalNav.Title>
                {item.items.map((nestedItem, nIdx) => renderSidebarItem(nestedItem, nIdx))}
            </Fragment>
        );

    return (
        <StyledSidebar data-testid="page sidebar">
            {skeletonShow ? (
                <SidebarSkeleton />
            ) : (
                ['top', 'bottom'].map((type) => (
                    <VerticalNav key={type}>
                        {getSidebarItems(isPlatformAllowed, isAccountRoleAssigned)[type].map((item, idx) =>
                            // eslint-disable-next-line no-nested-ternary
                            !item.isGroup
                                ? renderSidebarItem(item, idx)
                                : item.items.length
                                  ? renderGroup(item, idx)
                                  : null,
                        )}
                    </VerticalNav>
                ))
            )}
        </StyledSidebar>
    );
};

export default Sidebar;
